import { useState, useCallback } from 'react';
import axios from 'axios';
import { useSummarizer } from '@core/store/useSummarizer';
import { useToast, toast } from '../../components/ui/toast';

interface TranslationResponse {
  success: boolean;
  cta: boolean;
  message: string;
  data?: string;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

// Language code mapping for better API compatibility
const languageCodeMap: Record<string, string> = {
  'en-US': 'en',
  'es-ES': 'es',
  'fr-FR': 'fr',
  'de-DE': 'de',
  'zh-CN': 'zh',
  'ja-JP': 'ja',
  'ar-SA': 'ar',
  'ru-RU': 'ru',
  'it-IT': 'it',
  'pt-PT': 'pt',
  'nl-NL': 'nl',
  'pl-PL': 'pl',
  'tr-TR': 'tr',
  'vi-VN': 'vi',
  'th-TH': 'th',
  'id-ID': 'id',
  'ms-MY': 'ms',
  'hi-IN': 'hi',
  'bn-BD': 'bn',
  'ta-IN': 'ta',
};

export const useTranslation = () => {
  const {
    getTranslation,
    setTranslation,
    setIsTranslating,
    setTranslationError,
    markTranslationFailed,
    hasTranslationFailed,
    isTranslating,
    translationError,
    translations,
    currentSummary,
    apiBaseUrl,
    quota,
    summaryType,
    tone,
  } = useSummarizer();

  const { addToast } = useToast();
  const [loadingLanguage, setLoadingLanguage] = useState<string | null>(null);

  const translateContent = useCallback(
    async (
      content: string, 
      targetLanguage: string,
      detailLevel: string = summaryType,
      toneStyle: string = tone
    ): Promise<string | null> => {
      // Don't translate if target is English
      if (targetLanguage === 'en' || targetLanguage === 'en-US') {
        return content;
      }

      // Check if translation already exists for this combination
      const existingTranslation = getTranslation(targetLanguage, detailLevel, toneStyle);
      if (existingTranslation?.summary_as_md) {
        return existingTranslation.summary_as_md;
      }

      // Check if we've already failed to translate this combination
      if (hasTranslationFailed(targetLanguage, detailLevel, toneStyle)) {
        return null;
      }

      // Map language code
      const mappedLanguage = languageCodeMap[targetLanguage] || targetLanguage;

      setIsTranslating(true);
      setLoadingLanguage(targetLanguage);
      setTranslationError(null);

      try {
        const response = await axios.post<TranslationResponse>(
          `${apiBaseUrl}/api/public/translate`,
          {
            content,
            target_language: mappedLanguage,
            detail_level: detailLevel,
            tone: toneStyle,
            user_id: quota?.user_id,
            summary_id: currentSummary?.id,
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.data.success && response.data.data) {
          const translatedContent = response.data.data;

          // Store translation with detail_level and tone
          setTranslation(targetLanguage, detailLevel, toneStyle, {
            summary: translatedContent,
            summary_as_md: translatedContent,
            summary_html: translatedContent,
            tldr: undefined, // We don't translate TLDR separately for now
          });
          return translatedContent;
        } else {
          throw new Error(response.data.message || 'Translation failed');
        }
      } catch (error) {
        let errorMessage = 'Failed to translate content';

        if (axios.isAxiosError(error)) {
          if (error.response?.status === 429) {
            errorMessage = 'Daily translation quota exceeded. Please try again tomorrow.';
            // Trigger CTA dialog if needed
            const summarizerStore = useSummarizer.getState();
            summarizerStore.triggerCTADialog('quota_exceeded', 'translation', errorMessage);
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          }
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }

        setTranslationError(errorMessage);
        addToast(toast.error(errorMessage));
        // Mark this translation combination as failed to prevent retry
        markTranslationFailed(targetLanguage, detailLevel, toneStyle);
        return null;
      } finally {
        setIsTranslating(false);
        setLoadingLanguage(null);
      }
    },
    [
      getTranslation,
      setTranslation,
      setIsTranslating,
      setTranslationError,
      markTranslationFailed,
      hasTranslationFailed,
      addToast,
      apiBaseUrl,
      summaryType,
      tone,
    ]
  );

  const translateSummary = useCallback(
    async (targetLanguage: string, detailLevel?: string, toneStyle?: string) => {
      if (!currentSummary) {
        return null;
      }

      // Use summary_as_md if available, otherwise use summary
      const contentToTranslate = currentSummary.summary_as_md || currentSummary.summary;

      if (!contentToTranslate) {
        return null;
      }

      return translateContent(
        contentToTranslate, 
        targetLanguage,
        detailLevel || summaryType,
        toneStyle || tone
      );
    },
    [currentSummary, translateContent, summaryType, tone]
  );

  const getTranslatedContent = useCallback(
    (language: string, detailLevel?: string, toneStyle?: string) => {
      const currentDetailLevel = detailLevel || summaryType;
      const currentTone = toneStyle || tone;
      
      // Return original content for English
      if (language === 'en' || language === 'en-US') {
        if (currentSummary) {
          return {
            summary: currentSummary.summary,
            summary_as_md: currentSummary.summary_as_md || currentSummary.summary,
            summary_html: currentSummary.summary,
            detail_level: currentDetailLevel,
            tone: currentTone,
            language: 'en',
            timestamp: Date.now(),
          };
        }
        return null;
      }

      // Return translated content if available
      return getTranslation(language, currentDetailLevel, currentTone);
    },
    [currentSummary, getTranslation, summaryType, tone]
  );

  const isLanguageTranslated = useCallback(
    (language: string, detailLevel?: string, toneStyle?: string) => {
      if (language === 'en' || language === 'en-US') {
        return true; // English is always available
      }
      
      const currentDetailLevel = detailLevel || summaryType;
      const currentTone = toneStyle || tone;
      const translation = getTranslation(language, currentDetailLevel, currentTone);
      
      return !!translation;
    },
    [getTranslation, summaryType, tone]
  );

  const isLanguageAttempted = useCallback(
    (language: string, detailLevel?: string, toneStyle?: string) => {
      // Check if we've already tried to translate (either succeeded or failed)
      if (language === 'en' || language === 'en-US') {
        return true;
      }
      
      const currentDetailLevel = detailLevel || summaryType;
      const currentTone = toneStyle || tone;
      
      return isLanguageTranslated(language, currentDetailLevel, currentTone) || 
             hasTranslationFailed(language, currentDetailLevel, currentTone);
    },
    [isLanguageTranslated, hasTranslationFailed, summaryType, tone]
  );

  return {
    translateContent,
    translateSummary,
    getTranslatedContent,
    isLanguageTranslated,
    isLanguageAttempted,
    isTranslating,
    loadingLanguage,
    translationError,
    translations,
  };
};
